import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster } from 'sonner'

import './i18n'
import './styles/globals.css'

import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider, useAuth } from '@/lib/auth/context'
import { useExtensionBridge } from '@/lib/extension/bridge'
import { ScoreReauthDialog } from '@/features/auth/ScoreReauthDialog'

import { routeTree } from './routeTree.gen'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status =
          error && typeof error === 'object' && 'status' in error
            ? Number((error as { status?: number }).status ?? 0)
            : 0
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    // Placeholder: set at runtime by `InnerApp` below. All route loaders
    // receive this object and can use it to read auth state.
    auth: null as ReturnType<typeof useAuth> | null,
    queryClient,
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const auth = useAuth()
  // Pair with the Sprint browser extension (Score submission relay), keeping
  // it in sync across logins and logouts.
  useExtensionBridge()
  return (
    <>
      <RouterProvider router={router} context={{ auth, queryClient }} />
      <ScoreReauthDialog />
      <Toaster
        richColors
        closeButton
        position="bottom-right"
        toastOptions={{ duration: 4500 }}
      />
    </>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error(
    'Root element #root not found. Did you forget to update index.html?',
  )
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider delayDuration={200}>
          <InnerApp />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
